/* eslint-disable no-unused-vars */
/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, message, Spin } from "antd";
import { DropDownComponent } from "../Master/CommonComponents";
import { getHubList } from "../../services/api/hub";
import { HubCPOverviewService } from "../../services/api/hubCPOverviewService";
import TopCard from "./TopCard";
import StatusDistribution from "./StatusDistribution";
import AgeingBuckets from "./AgeingBuckets";
import Exceptions from "./Exceptions";
import ShipmentDetailsModal from "./ShipmentDetailsModal";
import { TOP_CARD_NAMES } from "../../constants/hubManagerOverviewConstants";


const HubCPOverview = () => {
    const [hubList, setHubList] = useState([]);
    const navigate = useNavigate();
    const [selectedHubs, setSelectedHubs] = useState(null);
    const [hubUser] = useState(() => {
        try {
            const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
            return user?.roles?.[0];
        } catch (e) {
            return null;
        }
    });
    const [hubLoading, setHubLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const ALL_HUBS_VALUE = "ALL_HUBS";



    // Transform dashboard data to card format
    const cards = useMemo(() => {
        if (!dashboardData || !Array.isArray(dashboardData)) {
            return [];
        }

        return dashboardData.map(item => ({
            id: item.title,
            name: item.title ? item.title.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) : "",
            value: item.totalCount || 0,
            visible: true,
            statuses: item.statuses || []
        }));
    }, [dashboardData]);

    // Fetch hub list on component mount
    useEffect(() => {
        const fetchHubList = async () => {
            try {
                setHubLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (error) {
                message.error("Failed to fetch hub list");
                console.error("Error fetching hub list:", error);
            } finally {
                setHubLoading(false);
            }
        };

        fetchHubList();
    }, []);

    // Set user role and default hub for HUBADMIN/HUBOPERATOR users
    useEffect(() => {
        if (hubList.length === 0) return;

        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userId = JSON.parse(localStorage.getItem("userId") || "{}");
        const userRole = user?.roles?.[0];
        // hubUser is already set via initial state, but we use userRole here for logic

        // Only set default hub if no hub is currently selected
        if (selectedHubs !== null) return;

        if (userRole === "ROLE_HUBADMIN" || userRole === "ROLE_HUBOPERATOR") {
            const defaultHub = hubList.find(
                (hub) => String(hub.value) === String(userId?.userInfo?.premiseId)
            );
            if (defaultHub) {
                setSelectedHubs(defaultHub.value);
            }
        } else {
            // For other users, default to "All Hubs" (null)
            setSelectedHubs(null);
        }
    }, [hubList]); // Only run when hubList changes

    // Prepare options with "All Hubs" option
    const hubOptions = React.useMemo(() => {
        if (hubList.length === 0) return [];

        const allHubsOption = {
            label: "All Hubs",
            value: ALL_HUBS_VALUE,
        };

        return [allHubsOption, ...hubList];
    }, [hubList]);

    // Check if all hubs are selected (when selectedHubs is null or ALL_HUBS_VALUE)
    const areAllHubsSelected = React.useMemo(() => {
        return selectedHubs === null || selectedHubs === ALL_HUBS_VALUE;
    }, [selectedHubs]);

    // Get hub parameter for API - null if all hubs selected, otherwise the selected hub
    const hubParamForAPI = React.useMemo(() => {
        return areAllHubsSelected ? null : (selectedHubs ? [selectedHubs] : null);
    }, [selectedHubs, areAllHubsSelected]);

    // Get display value - show "All Hubs" if null, otherwise the selected hub
    const displayValue = React.useMemo(() => {
        if (areAllHubsSelected) {
            return ALL_HUBS_VALUE;
        }
        return selectedHubs;
    }, [selectedHubs, areAllHubsSelected]);

    const handleHubChange = (value) => {
        // Handle empty selection or null
        if (!value || value === ALL_HUBS_VALUE) {
            setSelectedHubs(null);
            return;
        }

        // Single hub selected
        setSelectedHubs(value);
    };

    // Ref for AbortController
    const abortControllerRef = React.useRef(null);

    // Fetch dashboard data when hubs are selected
    useEffect(() => {
        if (selectedHubs === null) {
            // Still fetch data when "All Hubs" is selected (null means all hubs)
            // Don't return early, let it proceed to fetch for all hubs
        }

        const fetchDashboardData = async () => {
            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            // Create new controller
            abortControllerRef.current = new AbortController();
            const { signal } = abortControllerRef.current;

            try {
                setDashboardLoading(true);

                // Prevent initial "All Hubs" fetch for Hub Admins/Operators who are waiting for their specific hub to be auto-selected
                if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs === null) {
                    setDashboardLoading(false);
                    return;
                }

                // Determine hub parameter for API
                const hubParam = areAllHubsSelected ? null : (selectedHubs ? selectedHubs : null);

                const [dashboardResponse, pieChartResponse] = await Promise.all([
                    HubCPOverviewService.getHubOverviewDashboard(hubParam, { signal }),
                    HubCPOverviewService.getPieChartHubManagerOverview(hubParam, { signal })
                ]);

                if (dashboardResponse?.data) {
                    setDashboardData(dashboardResponse.data);
                } else {
                    setDashboardData([]);
                }

                if (pieChartResponse) {
                    setPieChartData(pieChartResponse);
                } else {
                    setPieChartData([]);
                }
            } catch (error) {
                if (error.name === 'CanceledError' || error.code === "ERR_CANCELED") {
                    console.log("Request canceled");
                    return;
                }
                message.error("Failed to fetch dashboard data");
                console.error("Error fetching dashboard data:", error);
                setDashboardData([]);
            } finally {
                // Only turn off loading if not canceled (checking if this is still the active request would be ideal, 
                // but checking signal.aborted is a good proxy if we are inside the scope, 
                // actually if aborted we probably shouldn't set loading false if a new one started? 
                // But the new one will set it to true. 
                if (!signal.aborted) {
                    setDashboardLoading(false);
                }
            }
        };

        fetchDashboardData();

        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };

    }, [selectedHubs, areAllHubsSelected]);

    // Handle card click - fetch clickable data
    const handleCardClick = async (card) => {
        const cardName = card.name.trim();
        // Check for specific cards to navigate
        const hubIdToUse = areAllHubsSelected ? null : selectedHubs;

        if (cardName.includes(TOP_CARD_NAMES.TOTAL_AVAILABLE_SHIPMENTS)) {
            navigate("/total-available-shipments-clickable-report", { state: { hubId: hubIdToUse } });
            return;
        }
        // Temporary hidden as API integration is pending
        // if (cardName.includes(TOP_CARD_NAMES.INBOUND_PENDENCY)) {
        //     navigate("/inbound-pendency-clickable-report", { state: { hubId: hubIdToUse } });
        //     return;
        // }
        // if (cardName.includes(TOP_CARD_NAMES.OUTSCAN_PENDING_INSCAN_TO_HUB)) {
        //     navigate("/outscan-pending-inscan-to-hub-clickable-report", { state: { hubId: hubIdToUse } });
        //     return;
        // }
        if (cardName.includes(TOP_CARD_NAMES.OUTSCAN_PENDING_INSCAN_TO_CP)) {
            navigate("/outscan-pending-inscan-to-cp-clickable-report", { state: { hubId: hubIdToUse } });
            return;
        }
    };




    return (
        <div className="hub-cp-overview-container">
            {/* Unified Filter Section */}
            <div className="dashboard-filters-wrapper">
                <div className="dashboard-filters">
                    <Row gutter={[16, 0]} justify="space-between" align="middle">
                        <Col xs={24} sm={24} md={14} lg={16} xl={18}>
                            {/* Tabs removed */}
                        </Col>
                        <Col xs={24} sm={24} md={10} lg={8} xl={6}>
                            <div className="hub-selection-wrapper">
                                <div className="filter-label">Select Hubs</div>
                                <DropDownComponent
                                    className="small-dropdown"
                                    placeholder="Select Hubs"
                                    options={hubOptions}
                                    onChange={handleHubChange}
                                    value={displayValue}
                                    allowClear
                                    mode="default"
                                    disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
                                    maxTagCount="responsive"
                                    showSearch={true}
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label || "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    loading={hubLoading}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <Spin spinning={dashboardLoading}>
                <TopCard cards={cards} onCardClick={handleCardClick} loading={false} />
                <Row gutter={[16, 16]} className="dashboard-content-row">
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <StatusDistribution data={pieChartData} selectedHubs={selectedHubs} areAllHubsSelected={areAllHubsSelected} />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <AgeingBuckets />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Exceptions />
                    </Col>
                </Row>
            </Spin>
        </div>

    );
};

export default HubCPOverview;

